import React, { useEffect, useState } from 'react';

import { useAuth } from '@/providers/AuthProvider';
import { SettingsList, SettingsListItems } from '@/components/shared/SettingCard';
import { App, AppState } from '@capacitor/app';
import { Formik } from 'formik';
import { useSnackbar } from 'notistack';

import { useOneSignal } from '../../../providers/OnesignalProvider';
import { AsaphSwitch } from '../../shared/Switch';
import { AccountDisplayProps } from '../AccountPanel';
import styles from './SettingPushNotifications.module.scss';

export const SettingPushNotifications: React.FC<AccountDisplayProps> = () => {
	const { user, updateUser } = useAuth();
	const { userPlayerId, getPermissionState, showPermissionPrompt } =
		useOneSignal();
	const [isPushEnabled, setPushEnabled] = useState(false);
	const [userPreferences, setUserPreferences] = useState({
		chat: false,
		groupChat: false,
		videoChat: false,
		videoChatActiveOnly: false,
		reEngagementNudge: false,
	});
	const { enqueueSnackbar } = useSnackbar();

	useEffect(() => {
		getMainPermissionState();
		setUserPreferences(user.notificationPreferences);

		return () => {
			App.removeAllListeners();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		// Update local preferences when user changes the main permission
		setUserPreferences(user.notificationPreferences);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user.notificationId]);

	const updateNotificationId = async (notificationId: string) => {
		try {
			await updateUser({
				input: {
					notificationId: notificationId,
				},
			});
			setPushEnabled(notificationId ? true : false);
		} catch (err) {
			console.error(err);
			enqueueSnackbar(
				'Something went wrong while updating your preferences, please try again',
				{
					variant: 'error',
				},
			);
		}
	};

	const getMainPermissionState = (
		isPermissionSetManually: boolean = false,
	) => {
		getPermissionState().then(
			async (isEnabled) => {
				// Notifications enabled on device, and user already has a notificationId
				if (isEnabled && user.notificationId) {
					setPushEnabled(isEnabled);
				}

				// Notifications got enabled on device manually and user didn't have a notificationId before
				if (
					isEnabled &&
					isPermissionSetManually &&
					!user.notificationId
				) {
					await updateNotificationId(userPlayerId);
				}

				// Notifications weren't enabled on device after asking user to set the manually
				if (!isEnabled && isPermissionSetManually) {
					App.removeAllListeners();
				}
			},
			() => {},
		);
	};

	const handleMainPermissionChange = async () => {
		if (isPushEnabled) {
			await updateNotificationId('');
		} else {
			showPermissionPrompt().then(async (isEnabled) => {
				setPushEnabled(isEnabled);
				// Update user with newest notificationId from OneSignal
				if (isEnabled) {
					await updateNotificationId(userPlayerId);
				}

				// On iOS showPermissionPrompt() returns false immediately so we check the notification state
				// after user comes back to the app after enabling/disabling notifications
				if (!isEnabled) {
					App.addListener('appStateChange', (state: AppState) => {
						// If app was paused and becomes active, check permission state again
						if (state.isActive) {
							getMainPermissionState(true);
							App.removeAllListeners();
						}
					});
				}
			});
		}
	};

	return (
		<Formik
			initialValues={userPreferences}
			enableReinitialize={true}
			onSubmit={async (preferences) => {
				console.log('Updating preferences: ', preferences);
				try {
					await updateUser({
						input: {
							notificationPreferences: preferences,
						},
					});
				} catch (err) {
					console.error(err);
					enqueueSnackbar(
						'Something went wrong while updating your preferences, please try again',
						{
							variant: 'error',
						},
					);
				}
			}}
		>
			{({ values, handleChange, handleSubmit }) => {
				const items: SettingsListItems = [
					{
						type: 'setting',
						title: 'Push notifications',
						subtitle: 'Allow push notifications for your profile',
						trailingComponent: (
							<AsaphSwitch
								checked={isPushEnabled}
								onChange={handleMainPermissionChange}
							/>
						),
						noChevron: true,
					},
					// Video chat notifications — feature removed, commented out for potential future use
					// { type: 'title', title: 'Video Chat Notifications', hidden: !isPushEnabled },
					// { type: 'setting', title: 'Video chat messages', condition: isPushEnabled, noChevron: true,
					//   trailingComponent: <AsaphSwitch name="videoChat" checked={values.videoChat} onChange={e => { handleChange(e); handleSubmit(); }} /> },
					// { type: 'setting', title: 'Active videos only', condition: isPushEnabled, noChevron: true,
					//   trailingComponent: <AsaphSwitch name="videoChatActiveOnly" checked={values.videoChatActiveOnly} onChange={e => { handleChange(e); handleSubmit(); }} /> },
					{
						type: 'title',
						title: 'Messages Notifications',
						hidden: !isPushEnabled,
					},
					{
						type: 'setting',
						title: 'Direct messages',
						condition: isPushEnabled,
						trailingComponent: (
							<AsaphSwitch
								name="chat"
								checked={values.chat}
								onChange={e => { handleChange(e); handleSubmit(); }}
							/>
						),
						noChevron: true,
					},
					{
						type: 'setting',
						title: 'Group chat messages',
						condition: isPushEnabled,
						trailingComponent: (
							<AsaphSwitch
								name="groupChat"
								checked={values.groupChat}
								onChange={e => { handleChange(e); handleSubmit(); }}
							/>
						),
						noChevron: true,
					},
				];

				return (
					<form>
						<div className={styles['setting-push-notifications']}>
							<SettingsList items={items} />
						</div>
					</form>
				);
			}}
		</Formik>
	);
};
